import { createDemandDataItemRecord } from "@infinode/records/DemandDataItemRecord";
import {createPowerDataItemRecord} from "@infinode/records/PowerDataItemRecord";
import {createCostDataItemRecord} from "@infinode/records/CostDataItemRecord";

import { createEnergyDataItemRecord } from "@infinode/records/EnergyDataItemRecord";
import { createGenericRealtimeDataItem } from "@infinode/records/RealtimeDataItemRecord";
import {createTrendAnalysisDataItemRecord} from "@infinode/records/TrendAnalysisDataItemRecord";
import {createAnomalyDetectionDataItemRecord} from "@infinode/records/AnomalyDetectionDataItemRecord";


import DataStateRecord from "@infinode/records/reducers/DataStateRecord";
import { List } from "immutable";
import { isNil } from "lodash-es";
import moment from "moment";
import { DATA_ITEM_TIMESTAMP_PROP_NAME, DATA_TYPES, ANALYSIS_TYPES } from "src/infinode/constants/data";
import { REALTIME_DATA_VALUE_TYPE, REALTIME_DATA_VALUE_TYPES_ARRAY } from "src/infinode/constants/realtimeData";
import actions from "./actions";
import { filterData } from "./utils";

const getDataItemInterval = dataItems => {
  return dataItems.length > 1 ? dataItems[1].timestamp - dataItems[0].timestamp : 0;
};

const getStartEndItems = (action, parsedDataItems, createRecordFunc) => {
  const interval = getDataItemInterval(parsedDataItems);
  let prefix = [];
  let suffix = [];
  if (interval > 0) {
    const { dataRequestPayload } = action;
    const { startDateTime, endDateTime } = dataRequestPayload;
    const start = moment(startDateTime).unix();
    const end = moment(endDateTime).unix();

    let currentStart = parsedDataItems[0].timestamp;
    while (currentStart > start) {
      currentStart -= interval;
      prefix.unshift(createRecordFunc({ timestamp: currentStart }));
    }

    let currentEnd = parsedDataItems[parsedDataItems.length - 1].timestamp;
    while (currentEnd < end) {
      currentEnd += interval;
      suffix.push(createRecordFunc({ timestamp: currentEnd }));
    }
  }

  return { prefix, suffix };
};

const parseDataItems = (action, createRecordFunc, updateItemValuesFunc) => {
  const result = [];
  const { dataItems } = action;
  if (dataItems.length > 0) {
    const firstItem = dataItems[0];
    let currentTs = firstItem[DATA_ITEM_TIMESTAMP_PROP_NAME];
    let currentItem = { timestamp: firstItem[DATA_ITEM_TIMESTAMP_PROP_NAME] };
    // Loop each item and combine items with same timestamp
    dataItems.forEach(i => {
      // New timestamp coming, finish the last one
      if (!isNil(currentTs) && i[DATA_ITEM_TIMESTAMP_PROP_NAME] > currentTs) {
        result.push(createRecordFunc(currentItem));
        currentTs = i[DATA_ITEM_TIMESTAMP_PROP_NAME];
        currentItem = { timestamp: i[DATA_ITEM_TIMESTAMP_PROP_NAME] };
      }
      // Integrate value into current item
      currentItem = updateItemValuesFunc(currentItem, i);
    });
    // Don't forget the last item
    result.push(createRecordFunc(currentItem));
  }

  // Add the prefix and suffix
  const { prefix, suffix } = getStartEndItems(action, result, createRecordFunc);

  return filterData(List(prefix.concat(result, suffix)));
};

const getCreateRecordFunc = (dataType, dataRequestPayload) => {
  let func = createEnergyDataItemRecord;
  switch (dataType) {
    case DATA_TYPES.ENERGY:
      func = dataRequestPayload.dataSourceTypes ? createEnergyDataItemRecord : createGenericRealtimeDataItem;
      break;
    case DATA_TYPES.POWER:
      func = dataRequestPayload.dataSourceTypes ? createPowerDataItemRecord : createGenericRealtimeDataItem;
      break;
    case DATA_TYPES.COST:
      func = dataRequestPayload.dataSourceTypes ? createCostDataItemRecord : createGenericRealtimeDataItem;
      break;
    case ANALYSIS_TYPES.TREND_ANALYSIS:
      func = dataRequestPayload.dataSourceTypes ? createTrendAnalysisDataItemRecord : createGenericRealtimeDataItem;
      break;
    case ANALYSIS_TYPES.ANOMALY_DETECTION:
      func = dataRequestPayload.dataSourceTypes ? createAnomalyDetectionDataItemRecord : createGenericRealtimeDataItem;
      break;
    case DATA_TYPES.DEMAND:
      func = createDemandDataItemRecord;
      break;
    case DATA_TYPES.REALTIME:
      func = createGenericRealtimeDataItem;
      break;
    default:
      break;
  }
  return func;
};

const updateEnergyItemValuesFunc = (currentItem, sourceItem) => {
  if (sourceItem.dataSource) {
    const sourceType = `${sourceItem.dataSource[0].toLowerCase()}${sourceItem.dataSource.slice(1)}`;
    currentItem[sourceType] = sourceItem.value;
  }
  return currentItem;
};

const updateRealtimeEnergyItemValuesFunc = (currentItem, sourceItem) => {
  const sourceType = REALTIME_DATA_VALUE_TYPE.ACTIVE_POWER;
  currentItem[sourceType] = sourceItem.value;
  return currentItem;
};

const updateRealtimePowerItemValuesFunc = (currentItem, sourceItem) => {
  const sourceType = REALTIME_DATA_VALUE_TYPE.ACTIVE_POWER; // to fix
  currentItem[sourceType] = sourceItem.value;
  return currentItem;
};

const updateRealtimeCostItemValuesFunc = (currentItem, sourceItem) => {
  const sourceType = REALTIME_DATA_VALUE_TYPE.ACTIVE_POWER; //to fix
  currentItem[sourceType] = sourceItem.value;
  return currentItem;
};

const updateRealtimeTrendAnalysisItemValuesFunc = (currentItem, sourceItem) => {
  const sourceType = REALTIME_DATA_VALUE_TYPE.ACTIVE_POWER; //to fix
  currentItem[sourceType] = sourceItem.value;
  return currentItem;
};

const updateRealtimeAnomalyDetectionItemValuesFunc = (currentItem, sourceItem) => {
  const sourceType = REALTIME_DATA_VALUE_TYPE.ACTIVE_POWER; //to fix
  currentItem[sourceType] = sourceItem.value;
  return currentItem;
};

const updateDemandItemValuesFunc = (currentItem, sourceItem) => {
  REALTIME_DATA_VALUE_TYPES_ARRAY.forEach(t => {
    currentItem[t] = sourceItem[t];
  });
  return currentItem;
};

const updatePowerItemValuesFunc = (currentItem, sourceItem) => {
  if (sourceItem.dataSource) {
    const sourceType = `${sourceItem.dataSource[0].toLowerCase()}${sourceItem.dataSource.slice(1)}`;
    currentItem[sourceType] = sourceItem.value;
  }
  return currentItem;
};

const updateCostItemValuesFunc = (currentItem, sourceItem) => {
  if (sourceItem.dataSource) {
    const sourceType = `${sourceItem.dataSource[0].toLowerCase()}${sourceItem.dataSource.slice(1)}`;
    currentItem[sourceType] = sourceItem.value;
  }
  return currentItem;
};

const updateTrendAnalysisItemValuesFunc = (currentItem, sourceItem) => {
  if (sourceItem.dataSource) {
    const sourceType = `${sourceItem.dataSource[0].toLowerCase()}${sourceItem.dataSource.slice(1)}`;
    currentItem[sourceType] = sourceItem.value;
  }
  return currentItem;
};

const updateAnomalyDetectionItemValuesFunc = (currentItem, sourceItem) => {
  if (sourceItem.dataSource) {
    const sourceType = `${sourceItem.dataSource[0].toLowerCase()}${sourceItem.dataSource.slice(1)}`;
    currentItem[sourceType] = sourceItem.value;
  }
  return currentItem;
};

const updateRealtimeItemValuesFunc = (currentItem, sourceItem) => {
  currentItem[sourceItem.label] = sourceItem.value;
  return currentItem;
};

const getUpdateItemValuesFunc = (dataType, dataRequestPayload) => {
  let func = null;
  switch (dataType) {
    case DATA_TYPES.ENERGY:
      func = dataRequestPayload.dataSourceTypes ? updateEnergyItemValuesFunc : updateRealtimeEnergyItemValuesFunc;
      break;
    case DATA_TYPES.DEMAND:
      func = updateDemandItemValuesFunc;
      break;
    case DATA_TYPES.POWER:
      func = dataRequestPayload.dataSourceTypes ? updatePowerItemValuesFunc : updateRealtimePowerItemValuesFunc;
      break;
    case DATA_TYPES.COST:
      func = dataRequestPayload.dataSourceTypes ? updateCostItemValuesFunc : updateRealtimeCostItemValuesFunc;
      break;
  
    case DATA_TYPES.REALTIME:
      func = updateRealtimeItemValuesFunc;
      break;

    case ANALYSIS_TYPES.TREND_ANALYSIS:
      func = dataRequestPayload.dataSourceTypes ? updateTrendAnalysisItemValuesFunc : updateRealtimeTrendAnalysisItemValuesFunc;
      break;
    case ANALYSIS_TYPES.ANOMALY_DETECTION:
      func = dataRequestPayload.dataSourceTypes ? updateAnomalyDetectionItemValuesFunc : updateRealtimeAnomalyDetectionItemValuesFunc;
      break;
  
    default:
      break;
  }
  return func;
};

export default function dataReducer(state = DataStateRecord(), action) {
  const { isCurrentData } = action;
  switch (action.type) {
    case actions.GET_DATA_REQUEST: {
      return state.withMutations(sm => {
        sm.merge({ loading: true, error: null });
        sm.update("currentData", d => (isCurrentData ? List() : d));
        sm.set("compareData", List());
      });
    }
    case actions.GET_DATA_SUCCESS: {
      const { dataType, dataRequestPayload } = action;
      const createRecordFunc = getCreateRecordFunc(dataType, dataRequestPayload);
      const updateItemValuesFunc = getUpdateItemValuesFunc(dataType, dataRequestPayload);
      console.log(`GET_DATA_SUCCESS : newDataItems`);
      const newDataItems = parseDataItems(action, createRecordFunc, updateItemValuesFunc);
      console.log(newDataItems.toJS());

      return state.withMutations(sm => {
        sm.merge({ loading: false, error: null });
        sm.update(isCurrentData ? "currentData" : "compareData", d => newDataItems);
      });
    }

    case actions.DATA_ERROR:
      return state.merge({ loading: false, error: action.error });
    default:
      return state;
  }
}
